import { errorIsStatus, useClientPortalAPI, useSubmit } from "@certn/api";
import {
  ColumnDefinition,
  Container,
  DataTable,
  DropdownMenu,
  GhostIconButton,
  Grid,
  Layout,
  Loader,
  MenuItem,
  MenuSection,
  PrimaryButton,
  SecondaryTopNav,
  SectionMessage,
  Stack,
  SuccessTag,
  Text,
} from "@certn/cascadia-react";
import { showToast } from "@certn/cascadia";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { object } from "yup";

import { PageTitle } from "../../components/page-title/PageTitle";
import { Schemas } from "../../types";
import { getCommonTableColumns } from "../templates/utils";
import { HeaderCard } from "./HeaderCard";
import { CustomInstructionsFormSection } from "../templates/CustomInstructionsFormSection";
import { EDUCATION_DEFAULT_INSTRUCTIONS } from "../templates/customInstructionsConstants";
import { customInstructionsSchemaField } from "../templates/customInstructionsValidation";

export function EducationVerificationTemplateListView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useClientPortalAPI(
    "/api/internal/client-portal/check-arguments-templates/education-verification/"
  );

  const templates = (data ??
    []) as Schemas["EducationVerificationTemplateList"][];

  const columns: ColumnDefinition<
    Schemas["EducationVerificationTemplateList"]
  >[] = getCommonTableColumns<Schemas["EducationVerificationTemplateList"]>({
    translate: t,
    actions: ({ row }) => (
      <DropdownMenu
        triggerButton={
          <GhostIconButton
            testID={`actions-dropdown-${row.id}`}
            size="small"
            icon="more_horiz"
            aria-label={t("Expand menu")}
          />
        }
        placement="bottom right"
        onItemAction={(key) => {
          if (key === "edit") {
            navigate(`/settings/education-verification-templates/${row.id}`);
          }
        }}
      >
        <MenuSection>
          <MenuItem id="edit" testID="edit-action">
            <Text style="input/text-field">{t("Edit")}</Text>
          </MenuItem>
        </MenuSection>
      </DropdownMenu>
    ),
  });

  return (
    <Container layout="full-width">
      <PageTitle>{t("Education verification")}</PageTitle>
      {error && !errorIsStatus(error, 404) && (
        <SectionMessage
          type="danger"
          icon="error"
          sectionTitle={t("Unable to load education verification templates.")}
        >
          {String(error.message)}
        </SectionMessage>
      )}
      <DataTable
        isLoading={isLoading}
        columns={columns}
        rows={templates}
        aria-label={t("Education verification templates")}
        testId="education-verification-templates-table"
        onRowAction={(id) => {
          navigate(`/settings/education-verification-templates/${id}`);
        }}
      />
    </Container>
  );
}

const validationSchema = object().shape({
  custom_instructions: customInstructionsSchemaField(),
});

export function EducationVerificationTemplateDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { templateId } = useParams();

  const goBack = () => navigate("/settings/education-verification-templates");

  const { data, mutate, isLoading, error } = useClientPortalAPI(
    `/api/internal/client-portal/check-arguments-templates/education-verification/${templateId}/`
  );

  const { submit } = useSubmit(
    `/api/internal/client-portal/check-arguments-templates/education-verification/${templateId}/update/`,
    {
      showToastOnError: true,
      onSuccess: async () => {
        await mutate();
        showToast(t("Education verification template has been updated."), {
          type: "success",
        });
        goBack();
      },
    }
  );

  const template = data as
    | Schemas["EducationVerificationTemplateDetail"]
    | undefined;
  const checkArguments = template?.check_arguments as {
    verification_type?: string;
    custom_instructions?: string;
  } | null;
  const verificationType = checkArguments?.verification_type;

  if (isLoading) {
    return <Loader overlay />;
  }

  if (error || !template) {
    return (
      <Container>
        <SectionMessage
          type="danger"
          icon="error"
          sectionTitle={t("Unable to load education verification template.")}
        >
          {error ? String(error.message) : null}
        </SectionMessage>
      </Container>
    );
  }

  return (
    <Formik
      enableReinitialize
      initialValues={{
        custom_instructions: checkArguments?.custom_instructions ?? "",
      }}
      validationSchema={validationSchema}
      onSubmit={(values) =>
        submit({
          custom_instructions: values.custom_instructions || "",
        })
      }
    >
      {({ handleSubmit, isSubmitting, isValid, dirty }) => (
        <Layout
          layout="narrow"
          header={
            <SecondaryTopNav
              testID="close-button"
              leftIcon="close"
              onleftIconOnPress={goBack}
            >
              <PrimaryButton
                onPress={() => handleSubmit()}
                isLoading={isSubmitting}
                isDisabled={!isValid || !dirty}
              >
                {t("Save changes")}
              </PrimaryButton>
            </SecondaryTopNav>
          }
        >
          <Container>
            <Stack gap="space-3">
              <Text as="h1" style="global/title-medium">
                {t("Education verification")}
              </Text>
            </Stack>

            <Grid form>
              <HeaderCard
                name={template.name}
                trailing={
                  <SuccessTag testID="active-tag" type="subtle">
                    {t("Active")}
                  </SuccessTag>
                }
              />
              <CustomInstructionsFormSection
                fieldName="custom_instructions"
                defaultInstructionsPlaceholder={EDUCATION_DEFAULT_INSTRUCTIONS(
                  verificationType
                )}
              />
            </Grid>
          </Container>
        </Layout>
      )}
    </Formik>
  );
}
